<template>
    <app-layout>
        <page-header :title="trans('activities.overview.headline')" title-in-app-header>
            <div class="mt-3 flex flex-wrap items-start gap-3">
                <filter-bar v-model="queryParams" :config="filterBarConfig" class="min-w-0 flex-1">
                    <template #locations-option="{ option }">
                        <div class="flex items-center justify-between gap-3">
                            <span>{{ option.label }}</span>
                            <small class="text-muted">{{ option.iataCode }}</small>
                        </div>
                    </template>
                    <template #types-option="{ option }">
                        <div class="flex items-center justify-between" style="gap: 1rem">
                            <span>{{ option.label }}</span>
                            <small class="text-muted">{{ option.shortLabel }}</small>
                        </div>
                    </template>
                    <template #users-option="{ option, show }">
                        <truncated-label :show="show" :text="option.label" />
                    </template>
                </filter-bar>
                <export-menu
                    v-if="hasAnyPermission('activity.export')"
                    :active-query-params="queryParams"
                    :allowed-exports="['csv', 'xlsx']"
                    :no-item-selected-tooltip="trans('activities.overview.export.tooltip.no_item_selected')"
                    :selection="selection"
                    class="shrink-0"
                    export-group-key="activities-overview-export"
                    routeName="activities.export"
                />
            </div>
        </page-header>
        <data-table
            :columns="fields"
            :items="activities"
            :no-items-view="noItemsView"
            :query-params="queryParams"
            :row-clickable="false"
            selection-enabled="global-activities"
        >
            <template #date="{ item }">
                <td class="align-top">
                    <div class="text-muted">
                        <div class="text-nowrap">
                            {{ $filters.fullDate(item.date, currentLocale) }}
                        </div>
                        <small>
                            <icon icon="cil-clock" />
                            {{ $filters.time(item.date, currentLocale) }}</small
                        >
                    </div>
                </td>
            </template>

            <template #type="{ item }">
                <td>
                    <div v-if="item.asset" :title="item.asset?.type?.label">{{ item.asset?.type?.shortLabel }}</div>
                </td>
            </template>

            <template #designation="{ item }">
                <td>
                    <div v-if="item.asset">
                        <div>
                            {{ item.asset?.designation.label }}
                        </div>
                        <small class="text-dark text-nowrap">{{ trans('assets.overview.cell.serial_number') }}: {{ item.asset?.serialNumber }}</small>
                    </div>
                </td>
            </template>

            <template #location="{ item }">
                <td>
                    <div v-if="item.asset">
                        {{ item.asset?.location.iataCode }}
                    </div>
                </td>
            </template>

            <template #specific_location="{ item }">
                <specific-location-overview-cell :location="item.asset?.location" />
            </template>

            <template #area="{ item }">
                <area-location-overview-cell :location="item.asset?.location" />
            </template>

            <template #causer="{ item, style }">
                <td :style="style" class="align-top">
                    <user-information v-if="item.causer" :info="item.causer" />
                    <span v-else>-</span>
                </td>
            </template>

            <template #event="{ item, style }">
                <td :style="style" class="align-top">
                    <activity :item="item" />
                </td>
            </template>
        </data-table>
    </app-layout>
</template>
<script setup>
import PageHeader from '@/Components/PageHeader.vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import UserInformation from '@/Components/UserInformation.vue';
import DataTable from '@/Components/DataTable.vue';
import Activity from '@/Components/ActivityLog/Activity.vue';
import { computed, ref } from 'vue';
import { trans } from '@/mixins/i18n.js';
import { usePage } from '@inertiajs/vue3';
import FilterBar from '@/Components/FilterBar.vue';
import { when } from '@/Utils/when';
import TruncatedLabel from '@/Pages/WorkOrders/TruncatedLabel.vue';
import useSelectedItems from '@/Utils/UseSelectedItems';
import ExportMenu from '@/Components/ExportMenu.vue';
import SpecificLocationOverviewCell from '@/Components/SpecificLocationOverviewCell.vue';
import AreaLocationOverviewCell from '@/Components/AreaLocationOverviewCell.vue';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';

const page = usePage();
const activitiesMeta = computed(() => page.props.activities?.meta);
const selection = useSelectedItems('global-activities', activitiesMeta);
const queryParams = ref(null);
const locationsOptions = computed(() =>
    (page.props.locations?.data || []).map((loc) => ({
        label: loc.name,
        value: loc.icaoCode,
        iataCode: loc.iataCode,
        searchableContent: `${loc.iataCode}|${loc.icaoCode}|${loc.name}`,
    })),
);
const specificLocationsOptions = computed(() => (page.props.specificLocations?.data || []).map((loc) => ({ label: loc.name, value: loc.id })));
const usersOptions = computed(() =>
    (page.props.users?.data || []).map(({ name, id }) => ({
        label: name,
        value: id,
    })),
);
const typesOptions = computed(() => page.props.types?.data || []);

const filterBarConfig = computed(() => ({
    route: route('activities.index'),
    routeOptions: { only: ['activities'] },
    additionalData: () => ({ selection: selection.getSelectionAsQueryParams() }),
    onLoadSuccess: (data) => selection.setSelection(data.props.activities.meta.selection),
    defaultSort: { column: 'date', asc: false },
    filters: [
        { type: 'search', key: 'search', placeholder: trans('activities.overview.filter.placeholder') },
        { type: 'date-range', label: trans('activities.overview.cell.date'), startKey: 'start', endKey: 'end' },
        { type: 'multi-select', key: 'users', label: trans('activities.overview.cell.user'), options: usersOptions.value, showSearchbar: true },
        { type: 'multi-select', key: 'types', label: trans('assets.overview.filter.type'), options: typesOptions.value },
        { type: 'multi-select', key: 'locations', label: trans('dashboard.filter.locations'), options: locationsOptions.value, showSearchbar: true },
        {
            type: 'multi-select',
            key: 'specificLocations',
            label: trans('activities.overview.cell.specific_location'),
            options: specificLocationsOptions.value,
            showSearchbar: true,
        },
    ],
}));

const fields = computed(() =>
    [
        {
            key: 'date',
            label: trans('activities.overview.cell.date'),
            _style: 'width: 120px; min-width: 120px; max-width: 120px',
        },
        {
            key: 'causer',
            label: trans('activities.overview.cell.user'),
            _style: 'width: 200px; min-width: 200px; max-width: 200px',
        },
        {
            key: 'event',
            label: trans('activities.overview.cell.event'),
            sorter: false,
            _style: 'width: 350px; min-width:350px; max-width: 350px',
        },
        {
            key: 'type',
            label: trans('activities.overview.cell.type'),
            _style: 'width: 90px;min-width: 90px; max-width: 90px',
        },
        {
            key: 'designation',
            label: trans('activities.overview.cell.designation'),
            _style: 'width: 150px; min-width: 150px; max-width: 150px',
        },
        // Show column if there are multiple airports
        when(locations.value.data.length > 1, {
            key: 'location',
            label: trans('activities.overview.cell.location'),
            _style: 'width: 130px; min-width: 130px; max-width:130px',
        }),
        {
            key: 'specific_location',
            label: trans('activities.overview.cell.specific_location'),
        },
        when(activities.value?.meta.hasAreaLocation, {
            key: 'area',
            label: trans('locations.area.label'),
        }),
    ].filter(Boolean),
);

const noItemsView = ref({
    noItems: trans('activities.overview.results.empty'),
});

const activities = computed(() => page.props.activities);
const locations = computed(() => page.props.locations);
</script>
